import React, { useState, useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import PaginationControls from '../../components/PaginationControls';
import { getAllCerts, deleteCerts, updateCerts, createCerts } from '../../API/Crud/Certs';
import ConfirmationModal from '../../components/ConfirmationModal';
import DashboardTemplate from '../template/DashboardTemplate';
import { Dialog, DialogBackdrop, DialogPanel } from '@headlessui/react';
import { upload } from '../../services/ApiRouting';
import { useDropzone } from 'react-dropzone';
import { BiEdit, BiTrash } from 'react-icons/bi';

function Certs() {
  const [certs, setCerts] = useState({ data: [], current_page: 1, per_page: 5, total: 0, last_page: 1 });
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);
  const [token, setToken] = useState(null);
  const [selectedCerts, setSelectedCerts] = useState(null);
  const [pagination, setPagination] = useState({ currentPage: 1, perPage: 5, total: 0, lastPage: 1 });
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [certsToDelete, setCertsToDelete] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null); // single file path string or null
  const [isUploading, setIsUploading] = useState(false);

  const baseUrl = API_PUBLIC_URL;

  const {
    register,
    handleSubmit,
    control,
    reset,
    formState: { errors },
  } = useForm({
    defaultValues: { title: '', file: '' },
  });

  // ─── Token ───────────────────────────────────────────────────────────────────
  useEffect(() => {
    const storedToken = sessionStorage.getItem('token');
    if (storedToken) {
      setToken(storedToken);
    } else {
      setError('No token found. Please log in.');
    }
  }, []);

  useEffect(() => {
    if (!token) return;
    fetchCerts(1);
  }, [token]);

  // ─── Sync form when editing ───────────────────────────────────────────────────
  useEffect(() => {
    if (selectedCerts) {
      reset({ title: selectedCerts.title || '', file: selectedCerts.image || '' });
    }
  }, [selectedCerts, reset]);

  // ─── Fetch ────────────────────────────────────────────────────────────────────
  async function fetchCerts(page) {
    setLoading(true);
    try {
      const data = await getAllCerts(page);
      setCerts(data.data);
      setPagination({
        currentPage: data.data.current_page,
        perPage: data.data.per_page,
        total: data.data.total,
        lastPage: data.data.last_page,
      });
      setError(null);
    } catch (err) {
      setError(err.message || 'An unknown error occurred');
      toast.error('Failed to load Certs');
    } finally {
      setLoading(false);
    }
  }

  // ─── Upload (single file) ─────────────────────────────────────────────────────
  /**
   * acceptedFiles comes from react-dropzone as an array.
   * We only handle the first file since multiple=false.
   */
  const uploadFiles = async (acceptedFiles, field) => {
    const file = acceptedFiles[0]; // single file
    if (!file) return;

    setIsUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file, file.name);

      const response = await upload(`${API_URL}/crud/certs/fileUpload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      const serverFilePath = response?.fileId || response?.data?.file_path;

      if (serverFilePath) {
        setUploadedFile(serverFilePath);
        field.onChange(serverFilePath); // single value, not array
      }
    } catch (err) {
      console.error('Upload error:', err);
      toast.error('File upload failed');
    } finally {
      setIsUploading(false);
    }
  };

  const removeFile = (field) => {
    setUploadedFile(null);
    field.onChange('');
  };

  // ─── Parse image path from JSON ───────────────────────────────────────────────
  const parseImagePath = (raw) => {
    try {
      return JSON.parse(raw); // stored as JSON string
    } catch {
      return raw || null;
    }
  };

  // ─── CRUD ─────────────────────────────────────────────────────────────────────
  const handleDeleteCerts = async (certId) => {
    try {
      const response = await deleteCerts(certId);
      if (response.ok) {
        setCerts((prev) => ({ ...prev, data: prev.data.filter((c) => c.id !== certId) }));
        toast.success('Cert deleted successfully');
        resetForm();
      }
    } catch {
      toast.error('Failed to delete Cert');
    }
  };

  const onCreateSubmit = async (data) => {
    const payload = { ...data, file: uploadedFile };
    try {
      const response = await createCerts(payload);
      if (response.ok) {
        setShowModal(false);
        toast.success('Cert added successfully');
        setCerts((prev) => ({ ...prev, data: [...(prev.data || []), response.data] }));
        resetForm();
      }
    } catch (err) {
      console.error('Error creating Cert:', err);
      toast.error('Failed to create Cert');
    }
  };

  const onUpdateSubmit = async (data) => {
    const payload = { ...data, file: uploadedFile };
    try {
      const response = await updateCerts(selectedCerts.id, payload);
      if (response.status) {
        setShowEditModal(false);
        toast.success(response.message || 'Cert updated successfully');
        setCerts((prev) => ({
          ...prev,
          data: prev.data.map((c) => (c.id === selectedCerts.id ? response.data : c)),
        }));
        setSelectedCerts(null);
        resetForm();
      }
    } catch (err) {
      console.error('Error updating Cert:', err);
      toast.error('Failed to update Cert');
    }
  };

  // ─── Helpers ──────────────────────────────────────────────────────────────────
  const resetForm = () => {
    reset({ title: '', file: '' }, { keepErrors: false, keepDirty: false, keepTouched: false });
    setUploadedFile(null);
  };

  const handleEditCerts = (cert) => {
    setSelectedCerts(cert);
    const imagePath = parseImagePath(cert.image);
    setUploadedFile(imagePath);
    setShowEditModal(true);
  };

  const handleConfirmDelete = () => {
    if (certsToDelete) {
      handleDeleteCerts(certsToDelete.id);
      setShowConfirmModal(false);
      setCertsToDelete(null);
    }
  };

  const handleCancelDelete = () => {
    setShowConfirmModal(false);
    setCertsToDelete(null);
    resetForm();
  };

  const handleCloseModal = () => {
    setShowModal(false);
    resetForm();
  };

  const formatDate = (dateString) =>
    new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: '2-digit', day: '2-digit',
    });

  // ─── Dropzone Render (reused in both modals) ──────────────────────────────────
  const renderDropzone = (field) => {
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
      accept: { 'image/jpeg': ['.jpeg', '.jpg'], 'image/png': ['.png'] },
      multiple: false,
      onDrop: (acceptedFiles) => uploadFiles(acceptedFiles, field),
    });

    return (
      <>
        <div
          {...getRootProps()}
          className="dropzone border-2 border-dashed border-gray-300 p-6 mb-4 cursor-pointer hover:border-[#F2A549] transition-colors rounded-md"
        >
          <input {...getInputProps()} />
          <div className="text-center">
            {isDragActive ? (
              <p className="text-blue-600">Drop the file here...</p>
            ) : (
              <>
                <p className="text-gray-600">Drag & drop an image here, or click to select</p>
                <p className="text-sm text-gray-400 mt-1">Supports: JPG, JPEG, PNG</p>
              </>
            )}
          </div>
        </div>

        {isUploading && <p className="text-blue-600 mb-4">Uploading...</p>}

        {uploadedFile && (
          <div className="mb-4">
            <h4 className="font-medium mb-2">Uploaded File:</h4>
            <div className="flex items-center justify-between bg-gray-50 p-2 rounded">
              <img
                width={100}
                src={baseUrl + uploadedFile}
                alt="Uploaded"
                className="rounded-full object-cover w-20 h-20"
              />
              <button
                type="button"
                onClick={() => removeFile(field)}
                className="text-red-600 hover:text-red-800 text-sm cursor-pointer"
              >
                Remove
              </button>
            </div>
          </div>
        )}
      </>
    );
  };

  // ─── Shared modal field layout ────────────────────────────────────────────────
  const renderFormFields = () => (
    <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
      {/* Title */}
      <div className="sm:col-span-4 mb-3">
        <label htmlFor="title" className="block text-sm font-medium text-gray-900">Title</label>
        <div className="mt-2">
          <input
            id="title"
            type="text"
            {...register('title', { required: 'Title is required' })}
            className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 border outline-1 outline-gray-300 focus:outline-2 focus:outline-[#F2A549] sm:text-sm"
          />
          {errors.title && <span className="text-red-500 text-sm">{errors.title.message}</span>}
        </div>
      </div>

      {/* Image */}
      <div className="sm:col-span-4 mb-3">
        <label className="block text-sm font-medium text-gray-900">Image</label>
        <div className="mt-2">
          <Controller
            name="file"
            control={control}
            defaultValue=""
            rules={{
              validate: (value) => !!value || 'Image is required',
            }}
            render={({ field }) => renderDropzone(field)}
          />
          {errors.file && <span className="text-red-500 text-sm">{errors.file.message}</span>}
        </div>
      </div>
    </div>
  );

  // ─── Render ───────────────────────────────────────────────────────────────────
  return (
    <>
      <DashboardTemplate>
        <div className="flex flex-col">
          <ToastContainer />
          <div className="transition-all duration-500 flex-1">
            <div className="content p-2 md:p-5">
              <div className="my-4 mx-2">
                {loading ? (
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-10 animate-spin fill-orange-600 block mx-auto" viewBox="0 0 24 24">
                    <path d="M12 22c5.421 0 10-4.579 10-10h-2c0 4.337-3.663 8-8 8s-8-3.663-8-8c0-4.336 3.663-8 8-8V2C6.579 2 2 6.58 2 12c0 5.421 4.579 10 10 10z" />
                  </svg>
                ) : error ? (
                  <p className="text-red-500">Error: {error}</p>
                ) : (
                  <div className="font-[sans-serif]">
                    <div className="flex justify-between items-center mb-4">
                      <h1 className="text-2xl font-bold text-gray-900">Certificats</h1>
                      <button
                        className="bg-[#F2A549] font-normal text-black px-4 py-2 cursor-pointer rounded hover:bg-[#efb46f]"
                        onClick={() => setShowModal(true)}
                      >
                        Add New Cert
                      </button>
                    </div>

                    <div className="w-auto max-w-full overflow-x-auto">
                      <table className="border-collapse min-w-full border border-gray-300 rounded-lg">
                        <thead className="bg-[#F2A549]">
                          <tr className="text-center">
                            {['Image', 'Title', 'Date', 'Actions'].map((h) => (
                              <th key={h} className="p-4 font-normal text-black">{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {Array.isArray(certs.data) && [...certs.data].reverse().map((cert) => {
                            const imagePath = parseImagePath(cert.image);
                            return (
                              <tr className="border border-gray-300" key={cert.id}>
                                <td className="border border-gray-300 py-2">
                                  <img
                                    src={imagePath ? baseUrl + imagePath : '/images/gecam.png'}
                                    alt={cert.title}
                                    className="w-20 h-20 mx-auto object-cover rounded-full"
                                  />
                                </td>
                                <td className="text-center text-sm border border-gray-300">{cert.title}</td>
                                <td className="text-sm border border-gray-300 text-center">{formatDate(cert.created_at)}</td>
                                <td className="border border-gray-300 text-center">
                                  <div className="flex justify-center gap-4">
                                    <button title="Edit" onClick={() => handleEditCerts(cert)} className="cursor-pointer">
                                      <BiEdit size={25} />
                                    </button>
                                    <button
                                      title="Delete"
                                      className="cursor-pointer"
                                      onClick={() => { setCertsToDelete(cert); setShowConfirmModal(true); }}
                                    >
                                      <BiTrash size={25} />
                                    </button>
                                  </div>
                                </td>
                              </tr>
                            );
                          })}

                          {(!Array.isArray(certs.data) || certs.data.length === 0) && (
                            <tr>
                              <td colSpan="4" className="p-4 text-center text-gray-500 border border-gray-300">
                                No Certificats available.
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>

                    <PaginationControls
                      itemsPerPage={pagination.perPage}
                      totalItems={pagination.total}
                      currentPage={pagination.currentPage}
                      paginate={fetchCerts}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>

          <ConfirmationModal
            show={showConfirmModal}
            title={certsToDelete?.title || ''}
            onConfirm={handleConfirmDelete}
            onCancel={handleCancelDelete}
          />
        </div>

        {/* ── Create Modal ── */}
        <Dialog open={showModal} onClose={handleCloseModal} className="relative z-[9999]">
          <DialogBackdrop transition className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in" />
          <div className="fixed inset-0 z-10 w-full overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4 sm:p-0">
              <DialogPanel transition className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full max-w-[70%] data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in">
                <form onSubmit={handleSubmit(onCreateSubmit)}>
                  {renderFormFields()}
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button type="submit" className="inline-flex w-full justify-center cursor-pointer rounded-md bg-[#F2A549] px-3 py-2 text-sm font-semibold text-black shadow-xs hover:bg-[#f1c40f] sm:ml-3 sm:w-auto">
                      Create
                    </button>
                    <button type="button" onClick={handleCloseModal} className="mt-3 cursor-pointer inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto">
                      Cancel
                    </button>
                  </div>
                </form>
              </DialogPanel>
            </div>
          </div>
        </Dialog>

        {/* ── Edit Modal ── */}
        <Dialog open={showEditModal} onClose={() => setShowEditModal(false)} className="relative z-[9999]">
          <DialogBackdrop transition className="fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in" />
          <div className="fixed inset-0 z-10 w-screen overflow-y-auto">
            <div className="flex min-h-screen items-center justify-center p-4 sm:p-0">
              <DialogPanel transition className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full max-w-[70%] data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in">
                <form onSubmit={handleSubmit(onUpdateSubmit)}>
                  {renderFormFields()}
                  <div className="bg-gray-50 px-4 py-3 sm:flex sm:flex-row-reverse sm:px-6">
                    <button type="submit" className="inline-flex cursor-pointer w-full justify-center rounded-md bg-[#F2A549] px-3 py-2 text-sm font-semibold text-black shadow-xs hover:bg-[#efb46f] sm:ml-3 sm:w-auto">
                      Update
                    </button>
                    <button type="button" onClick={() => { setShowEditModal(false); resetForm(); }} className="mt-3 cursor-pointer inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-xs inset-ring inset-ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto">
                      Cancel
                    </button>
                  </div>
                </form>
              </DialogPanel>
            </div>
          </div>
        </Dialog>
      </DashboardTemplate>
    </>
  );
}

export default Certs;